import { Box, Icon, Link, Text } from '@factorialco/deprecated-design-system'
import { SessionAttendanceFragment } from 'generated/resources/graphql'
import moment from 'moment'

import Sidepanel from 'modules/core/components/Sidepanel'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { ModeTag } from './ModeTag'
import { StatusTag } from './StatusTag'

export function SessionSidePanel({
  children,
  session,
  open,
  onClose,
}: {
  children: React.ReactNode
  session: { name?: string }
  open: boolean
  onClose: (value: boolean) => void
}) {
  return (
    <Sidepanel
      width="wide"
      onClose={() => onClose(false)}
      closeLabel="close"
      title={session?.name}
      open={open}
    >
      {children}
    </Sidepanel>
  )
}

function getModalityDescription(modality?: string | null): {
  description: string
  icon: React.ReactElement
} {
  switch (modality) {
    case 'online':
      return {
        description: i18n.t('trainings.my_trainings_revamp.session_detail.modality.virtual'),
        icon: <Icon.Medium color="grey800" icon="laptop" />,
      }
    case 'inperson':
      return {
        description: i18n.t('trainings.my_trainings_revamp.session_detail.modality.on_site'),
        icon: <Icon.Medium color="grey800" icon="pin" />,
      }
    case 'mixed':
      return {
        description: i18n.t('trainings.my_trainings_revamp.session_detail.modality.hybrid'),
        icon: <Icon.Medium color="grey800" icon="user" />,
      }
    default:
      return {
        description: '',
        icon: <></>,
      }
  }
}

export function SessionSidePanelAttendance<SA extends SessionAttendanceFragment>({
  sessionAttendance,
  canEdit,
}: {
  canEdit: boolean
  sessionAttendance?: SA
}) {
  if (!sessionAttendance) return null

  return (
    <Box gap="s8">
      <Text size="300" weight="semibold">
        {i18n.t('trainings.my_trainings_revamp.session_detail.status')}
      </Text>
      <StatusTag sessionAttendance={sessionAttendance} canEditAttendance={canEdit} />
    </Box>
  )
}

export function SessionSidePanelBriefing({
  session: { startsAt, endsAt, dueDate, duration, modality, link, location, training },
}: {
  session: {
    startsAt?: string | null
    endsAt?: string | null
    duration?: string | null
    dueDate?: string | null
    modality?: string | null
    link?: string | null
    location?: string | null
    training?: {
      name: string
      description: string
    } | null
  }
}) {
  const start = dateToString(moment(startsAt), 'ui_time')
  const end = dateToString(moment(endsAt), 'ui_time')
  const date = startsAt ? dateToString(moment.utc(startsAt), 'ui_dmy_long') : '-'
  const endDate = dueDate ? dateToString(moment.utc(dueDate), 'ui_dmy_long') : '-'

  const mode = (() => {
    if (startsAt && dueDate && Boolean(startsAt) === Boolean(dueDate)) {
      return 'selfPaced'
    }
    return 'scheduled'
  })()
  const modalityDescription = getModalityDescription(modality)
  return (
    <Box gap="s32">
      <Box justifyContent="spaceBetween" flexDirection="row">
        <Box gap="s24" flexDirection="column">
          <Box gap="s8">
            <Text size="300" weight="semibold">
              {i18n.t('trainings.my_trainings_revamp.session_detail.course_name')}
            </Text>
            <Text.Normal>{training?.name}</Text.Normal>
          </Box>
          <Box gap="s8">
            <Text size="300" weight="semibold">
              {mode === 'selfPaced'
                ? i18n.t('trainings.my_trainings_revamp.session_detail.start_date')
                : i18n.t('trainings.my_trainings_revamp.session_detail.date')}
            </Text>
            <Text.Normal>{date}</Text.Normal>
          </Box>
          <Box gap="s8">
            <Text size="300" weight="semibold">
              {i18n.t('trainings.my_trainings_revamp.session_detail.location')}
            </Text>
            <Text.Normal>{location || '-'}</Text.Normal>
          </Box>
          {modality && (
            <Box gap="s8">
              <Text size="300" weight="semibold">
                {i18n.t('trainings.my_trainings_revamp.session_detail.modality.title')}
              </Text>
              <Box flexDirection="row" gap="s8">
                {modalityDescription.icon}
                <Text.Normal>{modalityDescription.description}</Text.Normal>
              </Box>
            </Box>
          )}
        </Box>
        <Box gap="s24" style={{ flexBasis: '40%' }} flexDirection="column">
          <Box gap="s8">
            <Text size="300" weight="semibold">
              {i18n.t('trainings.my_trainings_revamp.session_detail.type')}
            </Text>
            <ModeTag mode={mode} />
          </Box>
          {mode === 'selfPaced' ? (
            <Box gap="s8">
              <Text size="300" weight="semibold">
                {i18n.t('trainings.my_trainings_revamp.session_detail.end_date')}
              </Text>
              <Text.Normal>{endDate}</Text.Normal>
            </Box>
          ) : (
            <Box gap="s8">
              <Text size="300" weight="semibold">
                {i18n.t('trainings.my_trainings_revamp.session_detail.hour')}
              </Text>
              <Text.Normal>
                {i18n.t('trainings.my_trainings_revamp.session_detail.duration', {
                  start: start,
                  end: end,
                  duration: duration,
                })}
              </Text.Normal>
            </Box>
          )}
          <Box gap="s8">
            <Text size="300" weight="semibold">
              {i18n.t('trainings.my_trainings_revamp.session_detail.link')}
            </Text>
            {link ? (
              <Link.Href href={link} external target="_blank">
                <Text size="300" weight="medium" underlined color="textLink">
                  {i18n.t('trainings.trainings_revamp.event_sidepanel.link_label')}
                </Text>
              </Link.Href>
            ) : (
              '-'
            )}
          </Box>
        </Box>
      </Box>
      <Box gap="s8">
        <Text size="300" weight="semibold">
          {i18n.t('trainings.my_trainings_revamp.session_detail.description')}
        </Text>
        <Text.Normal overflowWrap>
          {training?.description ? (
            /* Input is sanitized in backend
        nosemgrep: react-dangerouslysetinnerhtml */
            <span dangerouslySetInnerHTML={{ __html: training?.description }} />
          ) : (
            '-'
          )}
        </Text.Normal>
      </Box>
    </Box>
  )
}
